import { request } from "utils/request"
import { createMap, addToMap, replaceInMap, removeFromMap, asyncAction, getRange } from "./common"
import { whiteTasks } from "."
import { getState } from "storeAccess"

const initialState = {
  assets: {},
  graphInfo: null,
  maintenanceItems: {},
}

export function reducer(state = initialState, action) {

  switch (action.type) {
    case "assets/fetch":
      return { ...state, assets: createMap(action.res) }
    case "assets/create":
      return { ...state, assets: addToMap(state.assets, action.res) }
    case "assets/edit":
      return { ...state, assets: replaceInMap(state.assets, action.res) }
    case "assets/remove":
      return { ...state, assets: removeFromMap(state.assets, action.res) }
    // Maintenance item related cases
    case "assets/createMaintenance":
      if (!action.res) return state;

      // Get the assetId from the response
      const assetIdForCreate = action.res.assetId;
      const maintenanceIdForCreate = action.res.id;

      // Standardize on array format for maintenance
      const currentMaintenanceForCreate = Array.isArray(state.assets[assetIdForCreate]?.maintenance)
        ? [...state.assets[assetIdForCreate].maintenance]
        : [];

      // Add the new maintenance item to the array
      const updatedMaintenanceForCreate = [...currentMaintenanceForCreate, action.res];

      return {
        ...state,
        assets: {
          ...state.assets,
          [assetIdForCreate]: {
            ...state.assets[assetIdForCreate],
            maintenance: updatedMaintenanceForCreate
          }
        },
        maintenanceItems: {
          ...state.maintenanceItems,
          [maintenanceIdForCreate]: action.res
        }
      }

    case "assets/updateMaintenance":
      if (!action.res) return state;

      // Get the assetId from the response
      const assetIdForUpdate = action.res.assetId;
      const maintenanceIdForUpdate = action.res.id;

      // Make sure we have the required IDs and asset exists
      if (!assetIdForUpdate || !maintenanceIdForUpdate || !state.assets[assetIdForUpdate]) {
        return state;
      }

      // Get current maintenance items (preferably as an object for easier updates)
      const assetMaintenanceFormat = Array.isArray(state.assets[assetIdForUpdate]?.maintenance);

      // Process based on storage format
      if (assetMaintenanceFormat) {
        // Array format - create a new array with the updated item
        const updatedMaintenanceArray = Array.isArray(state.assets[assetIdForUpdate]?.maintenance)
          ? state.assets[assetIdForUpdate].maintenance.map(item =>
              item.id === maintenanceIdForUpdate ? { ...action.res } : item)
          : [action.res];

        // If the item wasn't found in the array, add it
        if (!updatedMaintenanceArray.some(item => item.id === maintenanceIdForUpdate)) {
          updatedMaintenanceArray.push(action.res);
        }

        return {
          ...state,
          assets: {
            ...state.assets,
            [assetIdForUpdate]: {
              ...state.assets[assetIdForUpdate],
              maintenance: updatedMaintenanceArray
            }
          },
          maintenanceItems: {
            ...state.maintenanceItems,
            [maintenanceIdForUpdate]: { ...action.res }
          }
        };
      } else {
        // Object format - directly update the specific key
        return {
          ...state,
          assets: {
            ...state.assets,
            [assetIdForUpdate]: {
              ...state.assets[assetIdForUpdate],
              maintenance: {
                ...(state.assets[assetIdForUpdate].maintenance || {}),
                [maintenanceIdForUpdate]: { ...action.res }
              }
            }
          },
          maintenanceItems: {
            ...state.maintenanceItems,
            [maintenanceIdForUpdate]: { ...action.res }
          }
        };
      }
    case "assets/deleteMaintenance":
      // Extract maintenanceId and assetId from action args
      const maintenanceId = action.args[0];
      const assetId = action.args[1];

      // Return current state if we don't have necessary IDs or asset doesn't exist
      if (!assetId || !state.assets[assetId]) return state;

      // Get current maintenance items as array (or empty array if not defined)
      const currentMaintenance = Array.isArray(state.assets[assetId]?.maintenance)
        ? state.assets[assetId].maintenance
        : [];

      // Filter out the deleted maintenance item
      const updatedMaintenanceArray = currentMaintenance.filter(item => item.id !== maintenanceId);

      // Remove the item from maintenanceItems map
      const { [maintenanceId]: _removed, ...updatedMaintenanceItems } = state.maintenanceItems;

      return {
        ...state,
        assets: {
          ...state.assets,
          [assetId]: {
            ...state.assets[assetId],
            maintenance: updatedMaintenanceArray
          }
        },
        maintenanceItems: updatedMaintenanceItems
      };
    case "assets/completeMaintenance":
      if (!action.res) return state;

      // Extract IDs from the response
      const maintenanceIdForComplete = action.res.id;
      const assetIdForComplete = action.res.assetId;

      // Return state if we don't have necessary IDs or asset doesn't exist
      if (!assetIdForComplete || !state.assets[assetIdForComplete]) return state;

      // Get current maintenance items as array (or empty array if not defined)
      const currentMaintenanceForComplete = Array.isArray(state.assets[assetIdForComplete]?.maintenance)
        ? [...state.assets[assetIdForComplete].maintenance]
        : [];

      // Replace the maintenance item in the array with the one from backend
      const updatedMaintenanceForComplete = currentMaintenanceForComplete.map(item =>
        item.id === maintenanceIdForComplete ? action.res : item
      );

      return {
        ...state,
        assets: {
          ...state.assets,
          [assetIdForComplete]: {
            ...state.assets[assetIdForComplete],
            maintenance: updatedMaintenanceForComplete
          }
        },
        maintenanceItems: {
          ...state.maintenanceItems,
          [maintenanceIdForComplete]: action.res
        }
      };
    case "projectActions/removePlanned":
      // updating the state by utilizing the spread operator is essential to let the useSelector know that a variable is mutated.
      // AssetView is interested in knowing if graphInfo is updated. Therefore a spread operator is used in line 27 aswell.
      if (!state.graphInfo) return state

      const newGraphInfo = { ...state.graphInfo }

      // Iterate through each action in the list
      action.res.forEach(actionItem => {
        const idsToRemove = actionItem.allowedAssetIds

        // Process each asset ID that needs updating
        for (var i = 0; i < idsToRemove.length; i++) {
          const assetId = idsToRemove[i]
          if (newGraphInfo[assetId]) {
            newGraphInfo[assetId] = {
              ...newGraphInfo[assetId],
              projectActions: newGraphInfo[assetId].projectActions.filter(
                projectAction => projectAction.id !== actionItem.id
              )
            }
          }
        }
      });

      return { ...state, graphInfo: newGraphInfo }
    case "assets/fetchGraphInfo":
      return { ...state, graphInfo: createMap(action.res) }
    case "assets/editOrder":
      return state
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetchGraphInfo = asyncAction("assets/fetchGraphInfo", (_dispatch) => {
  const state = getState()
  const [after, before] = getRange(state.visual.scheduleMondayDate, true, state.visual.scheduleWithWeek)
  return request("GET", `/assets/graph-info?before=${before}&after=${after}`).then((res) => res)
})

export const remove = asyncAction("assets/remove", (dispatch, id) => {
  return request("DELETE", `/asset/${id}`).then(() => ({ id }))
})

export const fetch = asyncAction("assets/fetch", (_dispatch) => {
  return request("GET", "/assets").then((res) => res.items)
})

export const create = asyncAction("assets/create", (dispatch, input) => {
  return request("POST", "/assets", { input }).then((res) => res.asset)
})

export const edit = asyncAction("assets/edit", (dispatch, id, patch) => {
  return request("PATCH", `/asset/${id}`, { patch }).then((res) => res.asset)
})

export const editOrder = asyncAction("assets/editOrder", (dispatch, input) => {
  return request("POST", "/assets/order", { input }).then(() => {
    dispatch(fetch())
  })
})

export const createMaintenance = asyncAction("assets/createMaintenance", (dispatch, assetId, maintenanceData) => {
  return request("POST", `/asset/${assetId}/maintenance`, {
    input: maintenanceData
  }).then((res) => {
    // If the response contains a white_task, add it directly to the state without backend calls
    if (res.white_task || (res.maintenance_item && res.maintenance_item.white_task)) {
      const whiteTask = res.white_task || res.maintenance_item.white_task;
      dispatch({
        type: "whiteTasks/create",
        res: whiteTask
      });
    }

    // The response now includes id and assetId directly
    return res.maintenance_item || res;
  })
})

export const updateMaintenance = asyncAction("assets/updateMaintenance", (dispatch, maintenanceId, maintenanceData) => {
  // Remove the assetId and id fields, as the backend doesn't expect them in updates
  const { assetId: _assetId, id: _id, asset_id: _asset_id, ...dataToSend } = maintenanceData;

  return request("PUT", `/asset/maintenance/${maintenanceId}`, {
    input: dataToSend
  }).then((res) => {
    // If response contains a white_task, update it directly in the whiteTasks state without backend calls
    if (res.white_task || (res.maintenance_item && res.maintenance_item.white_task)) {
      dispatch({ type: "whiteTasks/create", res: res.white_task });
    }

    // Return the maintenance item from the response
    return res.maintenance_item || res;
  })
})

export const deleteMaintenance = asyncAction("assets/deleteMaintenance", (dispatch, maintenanceId, assetId) => {
  // First, try to find the maintenance item to check if it has a white_task
  // We need to do this before deleting the maintenance item
  return request("GET", `/asset/maintenance/${maintenanceId}`)
    .then((getRes) => {
      const whiteTaskId = getRes?.white_task?.id || getRes?.maintenance_item?.white_task?.id;

      // Now delete the maintenance item
      return request("DELETE", `/asset/maintenance/${maintenanceId}`)
        .then(() => {
          // If there's an associated white task, delete it as well
          if (whiteTaskId) {
            dispatch(whiteTasks.remove(whiteTaskId));
          }

          // Return a consistent structure with both IDs
          return {
            maintenanceId,
            assetId
          };
        });
    })
    .catch((error) => {
      // If we can't fetch the maintenance item (perhaps it's already deleted),
      // just try to delete it directly
      console.error("Error fetching maintenance item before deletion:", error);
      return request("DELETE", `/asset/maintenance/${maintenanceId}`)
        .then(() => {
          return {
            maintenanceId,
            assetId
          };
        });
    });
})

export const completeMaintenance = asyncAction("assets/completeMaintenance", (dispatch, maintenanceId, _assetId) => {
  return request("POST", `/asset/maintenance/${maintenanceId}/complete`)
    .then((res) => {
      // Return the maintenance item from response, similar to create/update
      if (res.white_task || (res.maintenance_item && res.maintenance_item.white_task)) {
        dispatch({ type: "whiteTasks/create", res: res.white_task });
      }
      return res.maintenance_item || res;
    });
})
